import PortfolioLineChart from "features/portfolio/PortfolioLineChart";
import TwoChartsTableLayout from "common/Layouts/TwoChartsTableLayout";
import usePageWrapper from "common/Home/PageWrapper/usePageWrapper.ts";
import { selectAccountsLoadingStates } from "@shared/slices/portfolio.slice";
import AccountsPieChart from "features/portfolio/Accounts/AccountsPieChart";
import AccountsTableContent from "features/portfolio/Accounts/AccountsTableContent";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import { portfolioFilters } from "@shared/slices/filters.slice";
import { FilterType, LanguageNameSpaces, PortfolioTab } from "@shared/types";
import Button from "common/Buttons/Button";
import PlusOutlined from "@ant-design/icons/PlusOutlined";
import { useRouteMatch } from "react-router-dom";
import { CREATE_ACCOUNT_PATH } from "common/constants";
import { AccountsPageContext } from "features/portfolio/Accounts/AccountsPage.context.ts";
import { AccountFormContext } from "@shared/features/common/AccountForm.context";
import { useBottomNavigator } from "utils/hooks/misc";
import BuyButton from "common/Buttons/BuyButton";
import Flex from "common/Boxes/Flex";
import { PageTabsContext } from "common/Tabs/PageTabs.context.ts";
import StickyHeader from "common/Headers/StickyHeader.tsx";
import styled, { useTheme } from "styled-components";
import LayoutSwitch from "common/Switch/LayoutSwitch.tsx";
import MenuOutlined from "@ant-design/icons/MenuOutlined";
import TableOutlined from "@ant-design/icons/TableOutlined";
import useSearchInput from "utils/hooks/useSearchInput.tsx";
import FiltersButton from "common/Buttons/FiltersButton.tsx";
import ExpandCollapse from "common/Animations/ExpandCollapse.tsx";
import useMedia from "utils/hooks/useMedia.ts";
import FiltersChips from "common/Filters/FiltersChips/FiltersChips.tsx";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import { PortfolioContext } from "features/portfolio/Portfolio.context.ts";
import AccountsSortSelect from "features/portfolio/Accounts/AccountsSortSelect.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useIsSubUser } from "@shared/utils/hooks/misc.ts";
import { useCreateAccountClick } from "features/portfolio/common/hooks.tsx";
import NewLookAccounts, {
  AccountsAllocation,
} from "features/portfolio/NewLook/NewLookAccounts.tsx";
import { NewLookMobileTop } from "features/portfolio/NewLook/styles.ts";
import PhoneAccounts from "features/portfolio/NewLook/PhoneAccounts.tsx";

const Accounts = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { setTabCount } = useContext(PageTabsContext) || {};
  const { accounts, isCardsView, setIsCardsView, isLoading } =
    useContext(AccountsPageContext)!;
  const { openCreateAccountDrawer, closeCreateAccountDrawer } =
    useContext(AccountFormContext)!;
  const { useMobileSearch } = useContext(PageWrapperContext)!;
  const { analytics } = useContext(AnalyticsContext);
  const { actions, selectors } = portfolioFilters;
  const { searchValue, setSearchValue } = useContext(PortfolioContext)!;
  usePageWrapper({
    filters: {
      loadingStatesSelector: selectAccountsLoadingStates,
      actions,
      selectors,
      hideFilterTypes: HIDE_FILTER_TYPES,
    },
    showSearch: true,
    hideMobileFilters: true,
  });
  const useFiltersChips = useMedia([`(max-width: 1200px)`], [true], false);
  const isFiltersSingleRowView = useMedia(
    [`(max-width: 550px)`],
    [true],
    false
  );
  const [checkUrl, setCheckUrl] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const isCreateAccount = !!useRouteMatch(CREATE_ACCOUNT_PATH);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const isSubUser = useIsSubUser();
  const { onCreateAccountClick, whopModal } = useCreateAccountClick();
  const { isNewLook } = useTheme();

  useEffect(() => {
    const pending = isNewLook && isLoading && !accounts.length;
    setTabCount?.(PortfolioTab.ACCOUNTS, pending ? undefined : accounts.length);
  }, [accounts, isLoading, isNewLook]);

  useEffect(() => {
    setTimeout(() => {
      setCheckUrl(true);
    }, 300);
  }, []);

  useEffect(() => {
    if (!checkUrl) return;
    if (isCreateAccount) {
      openCreateAccountDrawer();
    } else {
      closeCreateAccountDrawer();
    }
  }, [isCreateAccount, checkUrl]);

  useEffect(() => {
    analytics?.portfolioAccountsView({} as any);
  }, [analytics]);

  const actionButton = !isBottomNavigator && !isSubUser && (
    <Button
      type="primary"
      style={{ borderRadius: 6 }}
      onClick={onCreateAccountClick}
    >
      <PlusOutlined />
      {t("buttons.create")}
    </Button>
  );

  const accountsPieChart = <AccountsPieChart key="pieChart" />;
  const portfolioLineChart = <PortfolioLineChart key="lineChart" />;

  const [searchInput] = useSearchInput({
    placeholder: tCommon("labels.search_placeholder"),
    style: { width: 200 },
    lowerCaseValue: true,
    onChange: setSearchValue,
  });

  const layoutChangeButtons = useMemo(
    () => (
      <LayoutSwitch
        layoutIcons={[<MenuOutlined />, <TableOutlined />]}
        selectedLayoutIndex={isCardsView ? 1 : 0}
        onClick={() => setIsCardsView?.(!isCardsView)}
        style={{ height: BUTTONS_HEIGHT }}
      />
    ),
    [isCardsView]
  );

  const filtersChips = (
    <FiltersChips
      filtersActions={actions}
      filtersSelectors={selectors}
      hideFilterTypes={HIDE_FILTER_TYPES}
      style={FILTERS_STYLE}
    />
  );

  const filtersButton = useFiltersChips && !isFiltersSingleRowView && (
    <FiltersButton
      onClick={() => setShowFilters(!showFilters)}
      selectors={selectors}
    />
  );

  const getExpandableChipsContent = () => {
    return (
      <ExpandCollapse expanded={showFilters || isFiltersSingleRowView}>
        {filtersChips}
      </ExpandCollapse>
    );
  };

  const renderTopContainer = () => {
    if (isNewLook) {
      // No accounts: the empty state leads (no $0 donut above it).
      if (!isLoading && !accounts.length) return <></>;
      return (
        <NewLookMobileTop>
          <AccountsAllocation compact={true} />
        </NewLookMobileTop>
      );
    }
    return <div style={{ position: "relative" }}>{accountsPieChart}</div>;
  };

  const mainContent = (
    <AccountsTableContent
      renderTopContainer={isBottomNavigator ? renderTopContainer : undefined}
      searchFilter={searchValue}
    />
  );

  if (isNewLook && !isBottomNavigator) {
    return (
      <>
        <NewLookAccounts
          onCreateClick={isSubUser ? undefined : onCreateAccountClick}
          searchInput={useMobileSearch ? undefined : searchInput}
          mainContent={mainContent}
          hideFilterTypes={HIDE_FILTER_TYPES}
        />
        {whopModal}
      </>
    );
  }

  if (isNewLook && isBottomNavigator) {
    return (
      <div style={{ position: "relative", height: "100%" }}>
        <PhoneAccounts
          searchFilter={searchValue}
          onCreateAccountClick={onCreateAccountClick}
        />
        {!isSubUser && (
          <BuyButton
            onClick={onCreateAccountClick}
            style={{ position: "absolute", bottom: 16, lineHeight: 16 }}
          />
        )}
        {whopModal}
      </div>
    );
  }

  const getContent = () => {
    if (isBottomNavigator) {
      return (
        <div style={{ position: "relative", height: "100%" }}>
          {mainContent}
          {!isSubUser && (
            <BuyButton
              onClick={onCreateAccountClick}
              style={{ position: "absolute", bottom: 16, lineHeight: 16 }}
            />
          )}
        </div>
      );
    }
    return (
      <TwoChartsTableLayout
        mobileCharts={[portfolioLineChart, mainContent]}
        smallChartUpperLeft={accountsPieChart}
        largeChartUpperMiddle={portfolioLineChart}
        mainContent={mainContent}
        useMobileCards={false}
        hideMainContentCard={isCardsView}
        distanceFromHeader={DISTANCE_FROM_HEADER}
      />
    );
  };

  return (
    <>
      {!isBottomNavigator && (
        <StickyHeader style={STICKY_HEADER_STYLE}>
          <ActionsWrapper>
            {!useMobileSearch && searchInput}
            <AccountsSortSelect />
            {layoutChangeButtons}
            {filtersButton}
            {actionButton}
          </ActionsWrapper>
          {useFiltersChips && getExpandableChipsContent()}
        </StickyHeader>
      )}
      {getContent()}
      {whopModal}
    </>
  );
};

export default Accounts;

const STICKY_HEADER_STYLE = { padding: "0 16px" };
const FILTERS_STYLE = { padding: "0 0 12px" };

const HIDE_FILTER_TYPES = [FilterType.AMOUNT_TYPE];
const BUTTONS_HEIGHT = 32;
const HEADER_VERTICAL_PADDING = 12;
const DISTANCE_FROM_HEADER = BUTTONS_HEIGHT + HEADER_VERTICAL_PADDING * 2;

const ActionsWrapper = styled(Flex)<{ $isSmallScreen?: boolean }>`
  column-gap: 8px;
  width: 100%;
  justify-content: flex-end;
  padding: ${HEADER_VERTICAL_PADDING}px 0;
`;
